import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Check, Loader2, RefreshCw, Send } from "lucide-react";
import { HISTORY_CATEGORY } from "@/components/broadcast/HistorySlide";

const Toggle = ({ on, onChange, label, testid }) => (
  <button type="button" data-testid={testid} onClick={() => onChange(!on)} className="flex items-center gap-2 font-oswald text-xs font-bold uppercase tracking-widest text-gray-300">
    <span className={`relative inline-block h-5 w-9 shrink-0 rounded-full transition-colors ${on ? "bg-[#16a34a]" : "bg-gray-600"}`}><span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} /></span>{label}
  </button>
);

function DayCard({ day, onSaved }) {
  const [items, setItems] = useState(day.items);
  const [busy, setBusy] = useState(false);
  useEffect(() => setItems(day.items), [day]);
  const key = day.date_key;
  const save = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/history/${key}`, { items, status });
      onSaved(data);
      toast.success(status === "approved" ? `${day.date_label} approved for air` : "Saved as draft");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/history/${key}/regenerate`);
      onSaved(data);
      toast.success("New draft written");
    } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); } finally { setBusy(false); }
  };
  const upd = (i, patch) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const approved = day.status === "approved";
  return (
    <div className="rounded-sm border border-white/10 bg-black/30 p-4" data-testid={`history-day-${key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="font-oswald text-lg font-black uppercase tracking-wider text-white">{day.date_label}</span>
          <span className={`rounded-full px-2.5 py-0.5 font-oswald text-[10px] font-bold uppercase tracking-widest ${approved ? "bg-[#16a34a]/20 text-[#4ade80]" : day.status === "missing" ? "bg-white/10 text-gray-400" : "bg-[#f6a21c]/20 text-[#fbbf24]"}`} data-testid={`history-status-${key}`}>{day.status}</span>
          {day.error && <span className="text-[11px] text-red-400">{day.error}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid={`history-regenerate-${key}`} onClick={regen} disabled={busy} className="flex items-center gap-1.5 rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-200 hover:border-white/40 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Rewrite</button>
          <button type="button" data-testid={`history-save-${key}`} onClick={() => save("draft")} disabled={busy || !items.length} className="rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-200 hover:border-white/40 disabled:opacity-50">Save draft</button>
          <button type="button" data-testid={`history-approve-${key}`} onClick={() => save("approved")} disabled={busy || !items.length} className="flex items-center gap-1.5 rounded-sm bg-[#16a34a] px-3 py-1.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-white hover:bg-[#15803d] disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Approve</button>
        </div>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        {items.map((it, i) => (
          <div key={it.category} className={`rounded-sm border p-3 ${it.enabled ? "border-white/10 bg-white/[0.03]" : "border-white/5 opacity-50"}`} data-testid={`history-item-${key}-${it.category}`}>
            <div className="flex items-center justify-between">
              <span className="font-oswald text-[10px] font-bold uppercase tracking-widest text-[#2a74d8]">{HISTORY_CATEGORY[it.category]}</span>
              <span className="flex items-center gap-2">
                {it.confidence === "medium" && <span className="rounded-full bg-[#f6a21c]/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#fbbf24]" title="Model was less sure — double-check this one">check</span>}
                <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-400"><input type="checkbox" checked={it.enabled !== false} onChange={(e) => upd(i, { enabled: e.target.checked })} /> air</label>
              </span>
            </div>
            <div className="mt-2 flex gap-2">
              <input value={it.year ?? ""} onChange={(e) => upd(i, { year: e.target.value ? Number(e.target.value) : null })} className="w-20 rounded-sm border border-white/10 bg-black/40 px-2 py-1 font-oswald text-sm font-bold text-white" placeholder="Year" />
              <input value={it.title} onChange={(e) => upd(i, { title: e.target.value })} className="flex-1 rounded-sm border border-white/10 bg-black/40 px-2 py-1 font-oswald text-sm font-bold text-white" placeholder="Headline" />
            </div>
            <textarea value={it.text} onChange={(e) => upd(i, { text: e.target.value })} rows={4} className="mt-2 w-full rounded-sm border border-white/10 bg-black/40 px-2 py-1 text-sm text-gray-200" />
          </div>
        ))}
        {!items.length && <div className="text-xs text-gray-500">{day.status === "generating" ? "Drafting with AI… this takes about a minute." : "No facts yet — click Rewrite."}</div>}
      </div>
    </div>
  );
}

export function HistoryTab() {
  const [days, setDays] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [posting, setPosting] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/history/week", { params: { days: 7 } });
      setDays(data.days); setCfg(data.config);
    } catch { toast.error("Could not load history facts"); }
  };
  useEffect(() => { load(); }, []);
  const generating = (days || []).some((d) => d.status === "generating");
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [generating]);
  const setConfig = async (patch) => {
    const { data } = await api.put("/history/config", patch);
    setCfg(data);
  };
  const postNow = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/history/post-now");
      toast.success(data.skipped ? `Skipped: ${data.skipped}` : `Facebook: ${data.facebook} · X: ${data.twitter}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Post failed"); } finally { setPosting(false); }
  };
  return (
    <div className="space-y-4" data-testid="history-tab">
      <div className="glass rounded-sm p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-oswald text-sm font-bold uppercase tracking-widest text-white"><BookOpen className="h-4 w-4 text-[#ffd60a]" /> On This Day · History Facts</div>
            <p className="mt-1 max-w-2xl text-xs text-gray-400">Three facts a day — Georgia weather, U.S. weather and Georgia history — drafted by AI the week ahead. Edit anything, untick a fact to keep it off air, then <b>Approve</b>. The 6 AM social post and the broadcast slide only use approved days while review is required.</p>
          </div>
          {cfg && (
            <div className="flex flex-col items-end gap-2">
              <Toggle on={cfg.require_approval} onChange={(v) => setConfig({ require_approval: v })} label="Require approval before airing" testid="history-require-approval" />
              <Toggle on={cfg.auto_post} onChange={(v) => setConfig({ auto_post: v })} label="Auto-post at 6 AM with Daily Impact" testid="history-auto-post" />
              <button type="button" data-testid="history-post-now" onClick={postNow} disabled={posting} className="flex items-center gap-1.5 rounded-sm bg-white px-3 py-1.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-[#0a0f1c] hover:bg-gray-200 disabled:opacity-50">{posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Post today's card now</button>
              {cfg.last_result && <span className="text-[10px] text-gray-500">Last: {JSON.stringify(cfg.last_result).slice(0, 90)}</span>}
            </div>
          )}
        </div>
      </div>
      {!days ? <div className="text-xs text-gray-500" data-testid="history-loading">Loading…</div>
        : days.map((d) => <DayCard key={d.date_key} day={d} onSaved={(nd) => setDays((xs) => xs.map((x) => (x.date_key === nd.date_key ? nd : x)))} />)}
    </div>
  );
}
